import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseOffer from 'hooks/Merchandise/useMerchandiseOffer'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import MerchandiseLowStockLabel from 'components/Merchandise/MerchandiseLowStockLabel'
import MerchandiseFreeShippingLabel from 'components/Merchandise/MerchandiseFreeShippingLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import Group from 'components/utils/Group'
import PriceRowMerchandiseCardPriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandiseCardPriceStack'
import MerchandiseCardSkeleton from './MerchandiseCardSkeleton'
import { nonNullable, take, uniqueBy } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useOfferInViewTracking } from 'hooks/useOfferInViewTracking'

const Card = styled(Clickable)`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(240)} 1fr;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    grid-template-rows: ${rem(270)} 1fr;
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

const ImageContainer = styled.div`
  position: relative;
  height: 100%;

  > * {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  >:nth-child(2) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.2s;
  }

  // On hover, we want to show the 2nd image (if there is one)
  ${mediaHoverable} {
    &:hover {
      >:nth-child(2) {
        opacity: 1;
      }
    }
  }
`

const HeaderTileWrapper = styled.div`
  position: relative;
`

const TopLeftContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  gap: ${rem(4)};
  flex-direction: column;
  align-items: stretch;
  width: max-content;

  span {
    width: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
  }
`

const ColourOption = styled(Group)`
  padding: ${rem(2)};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.round};
  transition: border-color 0.2s;
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }
`

const ColourCircle = styled.div`
  height: ${rem(12)};
  width: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  id: string
  index: number
  prioritisedVariantIds?: Array<string>
}

function MerchandiseOfferCard(props: Props) {
  const { id, index, prioritisedVariantIds } = props
  const [merchandise, fetching, error] = useMerchandiseOffer(id, { prioritisedVariantIds })

  const { setTileRef, trackingInView } = useOfferInViewTracking()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (trackingInView && merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: merchandise,
        position: index,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView])

  const handleClick = useCallback(() => {
    if (merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: merchandise,
        position: index,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchandise, index])

  const colourOptions = useMemo(() => {
    const allColourOptions = nonNullable(
      merchandise?.variants.flatMap(variant =>
        variant.options?.filter((opt): opt is App.MerchandiseOfferColourOption => 'colour' in opt) ?? []
      ) ?? []
    )
    return uniqueBy(allColourOptions, option => option.colour)
  }, [merchandise?.variants])

  if (error) {
    return null
  }

  if (fetching || !merchandise) {
    return <MerchandiseCardSkeleton />
  }

  const [firstImage, secondImage] = merchandise.variants[0].images
  const brandName = merchandise.brand?.name

  return <Card to={`/shop/offer/${merchandise.slug}/${merchandise.id}`} onClick={handleClick}>
    <HeaderTileWrapper>
      <TopLeftContainer>
        <LabelGroup>
          <MerchandiseFreeShippingLabel variants={merchandise.variants} />
        </LabelGroup>
        <LabelGroup>
          <MerchandiseLowStockLabel variants={merchandise.variants} />
        </LabelGroup>
      </TopLeftContainer>

      <ImageContainer ref={setTileRef}>
        <Image
          image={firstImage}
          fit="center"
          objectFit="contain"
          width={270}
        />
        {secondImage && <Image
          image={secondImage}
          fit="center"
          objectFit="contain"
          width={270}
        />}
      </ImageContainer>
    </HeaderTileWrapper>
    <Group direction="vertical" verticalAlign="space-between" gap={16}>
      <VerticalSpacer gap={8}>
        <div>
          {brandName && <BodyText variant="small" wrap="truncate">{brandName}</BodyText>}
          <BodyText variant="large" weight="bold" lineClamp={2}>
            {merchandise.name}
          </BodyText>
        </div>

        {colourOptions.length > 1 && <Group direction="horizontal" gap={4}>
          {take(colourOptions, 8).map(option => (
            <ColourOption
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="center"
              key={option.id}
            >
              <ColourCircle style={{ backgroundColor: option.colour }} />
            </ColourOption>
          ))}
          {colourOptions.length > 8 && <BodyText variant="small" weight="bold" colour="neutral-two">
            +{colourOptions.length - 8} more
          </BodyText>}
        </Group>}
      </VerticalSpacer>

      <CSSBreakpoint min="tablet">
        <PriceRowMerchandiseCardPriceStack
          price={merchandise.variants[0].price}
          regularPrice={merchandise.variants[0].regularPrice}
          points={merchandise.variants[0].pointsPrice}
          pointsRegularPrice={merchandise.variants[0].pointsRegularPrice}
          variant={merchandise.variants[0]}
          size="L"
          direction="stack"
        />
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <PriceRowMerchandiseCardPriceStack
          price={merchandise.variants[0].price}
          regularPrice={merchandise.variants[0].regularPrice}
          points={merchandise.variants[0].pointsPrice}
          pointsRegularPrice={merchandise.variants[0].pointsRegularPrice}
          variant={merchandise.variants[0]}
          size="L"
          direction="stack"
        />
      </CSSBreakpoint>
    </Group>
  </Card>
}

export default MerchandiseOfferCard
